import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import CloseIcon from '@mui/icons-material/CloseRounded';

import { slideUp } from '../utils';
import styles from './AIImageGenerationCard.module.css';
import gestureLogo from '../../assets/gesture-logo-transparent-bg.png';

const AI_IMAGE_GENERATION_MESSAGE_MAX_LENGTH = 500;

const AIImageGenerationCard = ({ handleClose, initialValue, onGenerate }) => {
  const [message, setMessage] = useState(() => {
    const raw = initialValue ?? '';
    return raw.length > AI_IMAGE_GENERATION_MESSAGE_MAX_LENGTH
      ? raw.slice(0, AI_IMAGE_GENERATION_MESSAGE_MAX_LENGTH)
      : raw;
  });
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState(null);
  const [isFocused, setIsFocused] = useState(false);
  const [progress, setProgress] = useState(0);
  const [imageLoaded, setImageLoaded] = useState(false);
  const progressIntervalRef = useRef(null);
  const successTimeoutRef = useRef(null);

  const handleChange = (e) => {
    setError(null); // Clear error on new input
    const newValue = e.target.value;
    if (newValue.length <= AI_IMAGE_GENERATION_MESSAGE_MAX_LENGTH) {
      setMessage(newValue);
    }
  };

  const handleGenerate = async () => {
    const trimmed = message.trim().slice(0, AI_IMAGE_GENERATION_MESSAGE_MAX_LENGTH);
    if (!trimmed || isGenerating) {
      return;
    }

    setIsGenerating(true);
    setError(null); // Clear previous errors
    setProgress(0); // Reset progress

    // Start simulated progress
    progressIntervalRef.current = setInterval(() => {
      setProgress((prev) => {
        if (prev >= 90) return 90; // Cap at 90% until API completes
        const increment = Math.random() * 15; // Increment by 0-15% each interval
        return Math.min(90, prev + increment); // Ensure it never exceeds 90%
      });
    }, 1000); // Update every 1000ms

    try {
      if (onGenerate) {
        // Handle both sync and async onGenerate callbacks
        const result = onGenerate(trimmed);
        if (result && typeof result.then === 'function') {
          await result;
        }
      }

      // Clear progress interval and complete progress
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
        progressIntervalRef.current = null;
      }
      setProgress(100);

      // Small delay to show 100% before closing
      successTimeoutRef.current = setTimeout(() => {
        setIsGenerating(false);
        setProgress(0);
        successTimeoutRef.current = null;
      }, 300);
    } catch (error) {
      console.error('Error generating image:', error);

      // Clear progress interval on error
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
        progressIntervalRef.current = null;
      }
      // Clear success timeout if it exists
      if (successTimeoutRef.current) {
        clearTimeout(successTimeoutRef.current);
        successTimeoutRef.current = null;
      }
      setProgress(0);

      // Set error message for display
      setError(error.message || 'Failed to generate image. Please try again.');
      // Reset generating state on error so user can retry
      setIsGenerating(false);
    }
  };

  const handleFocus = () => {
    setIsFocused(true);
    document.body.classList.add(styles['fix-safari-position']);
  };

  const handleBlur = () => {
    setIsFocused(false);
    document.body.classList.remove(styles['fix-safari-position']);
  };

  // Cleanup: Remove Safari fix class on unmount if still applied
  useEffect(() => {
    return () => {
      document.body.classList.remove(styles['fix-safari-position']);
      // Cleanup progress interval on unmount
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
        progressIntervalRef.current = null;
      }
      // Cleanup success timeout on unmount
      if (successTimeoutRef.current) {
        clearTimeout(successTimeoutRef.current);
        successTimeoutRef.current = null;
      }
    };
  }, []);

  const charCount = message.length;
  const displayCount = Math.min(charCount, AI_IMAGE_GENERATION_MESSAGE_MAX_LENGTH);
  const isAtLimit = charCount >= AI_IMAGE_GENERATION_MESSAGE_MAX_LENGTH;
  const isDisabled = !message.trim() || isGenerating;

  return (
    <motion.div
      onClick={(e) => e.stopPropagation()}
      className={styles['edit-card']}
      variants={slideUp}
      initial='hidden'
      animate='visible'
      exit='exit'
      onAnimationStart={(definition) => {
        if (definition === 'visible') {
          window.scrollTo(0, 0);
          document.body.style.overflow = 'hidden';
        }
      }}
    >
      <CloseIcon
        className={styles['close-button']}
        fontSize='large'
        onClick={handleClose}
      />
      <div className={styles['content-wrapper']}>
        <div className={styles['content-container']}>
          <h1 className={styles['header-text']}>
            <span>Craft Your Vision</span>
            <span className={styles['sparkle']}>✨</span>
          </h1>
          <div className={`${styles['textarea-wrapper']} ${isFocused ? styles['textarea-wrapper-focused'] : ''}`}>
            <textarea
              className={styles['input-field']}
              value={message}
              onChange={handleChange}
              placeholder='e.g. You make every day feel like home. Happy first anniversary, my love.'
              onFocus={handleFocus}
              onBlur={handleBlur}
              disabled={isGenerating}
              maxLength={AI_IMAGE_GENERATION_MESSAGE_MAX_LENGTH}
            />
          </div>
          <div className={styles['character-counter-container']} role="status" aria-live="polite" aria-atomic="true">
            <span
              key={`count-${displayCount}-${isAtLimit}`}
              className={`${styles['character-counter']} ${isAtLimit ? styles['character-counter-limit'] : ''}`}>
              {displayCount} / {AI_IMAGE_GENERATION_MESSAGE_MAX_LENGTH}
            </span>
          </div>
          {error && (
            <div className={styles['error-message']} role="alert">
              {error}
            </div>
          )}
        </div>
        {isGenerating && (
          <div className={styles['progress-container']}>
            <div className={styles['progress-bar']}>
              <div
                className={styles['progress-fill']}
                style={{ transform: `scaleX(${Math.max(0, Math.min(1, progress / 100))})` }}
              />
            </div>
          </div>
        )}
        <div className={styles['button-container']}>
          <button
            className={`rounded-pill ${styles['generate-button']} ${isDisabled ? styles['generate-button-disabled'] : ''} ${isGenerating ? styles['generate-button-loading'] : ''}`}
            onClick={handleGenerate}
            disabled={isDisabled}
            aria-label={isGenerating ? "Generating image..." : "Generate Image"}
          >
            {isGenerating ? (
              <span className={styles['loading-content']}>
                <img
                  src={gestureLogo}
                  alt=""
                  className={`${styles['gesture-logo']} ${styles['growing']} ${imageLoaded ? styles['logo-loaded'] : ''}`}
                  aria-hidden="true"
                  onLoad={() => setImageLoaded(true)}
                />
                enerating...
              </span>
            ) : (
              <>
                <img
                  src={gestureLogo}
                  alt=""
                  className={`${styles['gesture-logo']} ${imageLoaded ? styles['logo-loaded'] : ''}`}
                  aria-hidden="true"
                  onLoad={() => setImageLoaded(true)}
                />
                enerate Image
              </>
            )}
          </button>
        </div>
      </div>
    </motion.div>
  );
};

export default AIImageGenerationCard;
